import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { ChevronDown, Menu } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isPathActive } from '../lib/utils';
import { Route } from '../types';
import { SubmenuItem } from './navigation-submenu-item';

type NavItemProps = {
  route: Route;
  currentPathname: string | null;
  isCompact?: boolean;
  onClick?: () => void;
};

export function NavItem({
  route,
  currentPathname,
  isCompact = false,
  onClick,
}: NavItemProps) {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const navItemRef = useRef<HTMLDivElement>(null);
  const isActive = isPathActive(currentPathname, route.path, route.subRoutes);

  const toggleSubmenu = () => setIsSubmenuOpen(!isSubmenuOpen);
  const closeSubmenu = useCallback(() => {
    setIsSubmenuOpen(false);
    onClick?.();
  }, [onClick]);

  useEffect(() => {
    if (!isSubmenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        navItemRef.current &&
        !navItemRef.current.contains(event.target as Node)
      ) {
        closeSubmenu();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSubmenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isSubmenuOpen, closeSubmenu]);

  const t = useI18n();
  const comingSoonText = t('navigation.comingSoon');

  // In compact mode (for dropdowns in responsive view), render a simplified version
  if (isCompact) {
    return (
      <div className='my-1'>
        {route.subRoutes ? (
          <>
            <button
              onClick={toggleSubmenu}
              className={cn(
                'flex w-full items-center justify-between rounded-md px-3 py-2 text-sm',
                isActive
                  ? 'bg-gray-100 text-primary dark:bg-gray-800'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
                route.comingSoon && 'cursor-not-allowed opacity-70'
              )}
              disabled={route.comingSoon}
            >
              <div className='flex items-center gap-2'>
                {route.comingSoon && (
                  <div className='absolute -top-5 left-3'>
                    <span className='text-xs text-gray-500 dark:text-gray-400'>
                      {comingSoonText}
                    </span>
                  </div>
                )}
                <route.icon className='h-4 w-4' aria-hidden='true' />
                <span>{route.title}</span>
              </div>
              <ChevronDown
                className={cn(
                  'h-4 w-4 text-gray-500 transition-transform duration-200',
                  isSubmenuOpen ? 'rotate-180' : ''
                )}
              />
            </button>

            {isSubmenuOpen && (
              <div className='ml-6 mt-1 flex flex-col space-y-1'>
                {route.subRoutes.map((subRoute) => (
                  <SubmenuItem
                    key={subRoute.path}
                    route={subRoute}
                    isSelected={currentPathname === subRoute.path}
                    onClick={closeSubmenu}
                  />
                ))}
              </div>
            )}
          </>
        ) : route.comingSoon ? (
          <div
            className={cn(
              'relative flex cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-sm opacity-70',
              isActive
                ? 'bg-gray-100 text-primary dark:bg-gray-800'
                : 'text-gray-700 dark:text-gray-300'
            )}
          >
            {route.comingSoon && (
              <div className='absolute -top-5 left-3'>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {comingSoonText}
                </span>
              </div>
            )}
            <route.icon className='h-4 w-4' aria-hidden='true' />
            <span>{route.title}</span>
          </div>
        ) : (
          <Link
            href={route.path}
            className={cn(
              'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
              isActive
                ? 'bg-gray-100 text-primary dark:bg-gray-800'
                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
            )}
            onClick={onClick}
          >
            <route.icon className='h-4 w-4' aria-hidden='true' />
            <span>{route.title}</span>
          </Link>
        )}
      </div>
    );
  }

  const itemClasses = cn(
    'relative flex items-center gap-2 px-3 pt-2 pb-[6px] text-sm font-medium transition-colors duration-150 rounded-md',
    'after:absolute after:bottom-0 after:left-3 after:right-3 after:h-[2px] after:transition-colors after:duration-150',
    isActive
      ? 'text-primary after:bg-primary hover:bg-gray-100 dark:hover:bg-gray-800'
      : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800 after:bg-transparent',
    route.comingSoon && 'opacity-70 cursor-not-allowed'
  );

  return (
    <div className='relative' ref={navItemRef}>
      {/* Standard navigation for desktop */}
      <div className='hidden md:block'>
        {route.subRoutes ? (
          <button
            onClick={route.comingSoon ? undefined : toggleSubmenu}
            className={itemClasses}
            aria-haspopup='true'
            aria-expanded={isSubmenuOpen}
            disabled={route.comingSoon}
          >
            {route.comingSoon && (
              <div className='absolute -top-5 left-3'>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {comingSoonText}
                </span>
              </div>
            )}
            <route.icon className='h-4 w-4' aria-hidden='true' />
            <span>{route.title}</span>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-gray-500 transition-transform duration-200',
                isSubmenuOpen ? 'rotate-180' : ''
              )}
              aria-hidden='true'
            />
          </button>
        ) : route.comingSoon ? (
          <div className={itemClasses}>
            {route.comingSoon && (
              <div className='absolute -top-5 left-3'>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {comingSoonText}
                </span>
              </div>
            )}
            <route.icon className='h-4 w-4' aria-hidden='true' />
            <span>{route.title}</span>
          </div>
        ) : (
          <Link href={route.path} className={itemClasses}>
            {route.comingSoon && (
              <div className='absolute -top-5 left-3'>
                <span className='text-xs text-gray-500 dark:text-gray-400'>
                  {comingSoonText}
                </span>
              </div>
            )}
            <route.icon className='h-4 w-4' aria-hidden='true' />
            <span>{route.title}</span>
          </Link>
        )}
      </div>

      {/* Mobile navigation - only icon on small screens, icon and text on medium */}
      <div className='md:hidden'>
        <button
          onClick={route.comingSoon ? undefined : toggleSubmenu}
          className={itemClasses}
          aria-haspopup='true'
          aria-expanded={isSubmenuOpen}
          disabled={route.comingSoon}
        >
          {route.comingSoon && (
            <div className='absolute -top-5 left-3'>
              <span className='text-xs text-gray-500 dark:text-gray-400'>
                {comingSoonText}
              </span>
            </div>
          )}
          <route.icon className='h-4 w-4' aria-hidden='true' />
          <span className='hidden sm:inline'>{route.title}</span>
          <Menu
            className={cn(
              'h-4 w-4 text-gray-500 transition-transform duration-200',
              isSubmenuOpen ? 'rotate-180' : ''
            )}
            aria-hidden='true'
          />
        </button>
      </div>

      {isSubmenuOpen && (
        <div className='absolute left-0 top-full z-20 mt-2 w-64 origin-top-left rounded-lg border border-gray-200 bg-white p-2 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-900'>
          <div
            className='flex flex-col space-y-1'
            role='menu'
            aria-orientation='vertical'
            aria-labelledby='options-menu'
          >
            {/* Always show route as first item in mobile dropdown if not a parent route */}
            {!route.subRoutes && (
              <SubmenuItem
                key={route.path}
                route={{
                  title: route.title,
                  path: route.path,
                  icon: route.icon,
                  description: '',
                  comingSoon: route.comingSoon,
                }}
                isSelected={currentPathname === route.path}
                onClick={closeSubmenu}
              />
            )}

            {/* Show subroutes if available */}
            {route.subRoutes &&
              route.subRoutes.map((subRoute) => (
                <SubmenuItem
                  key={subRoute.path}
                  route={subRoute}
                  isSelected={currentPathname === subRoute.path}
                  onClick={closeSubmenu}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
